import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { allApis } from "../../../request/config";
import apiRequest from "../../../request/apiRequest/apiRequest";
import { ObjectValues } from "../../../pages/login";

export type LevelTypes =
  | "exam"
  | "goal"
  | "section"
  | "unit"
  | "topic"
  | "subtopic"
  | "cluster"
  | "question";
export type MockLevelTypes = "series" | "mocktest" | "section" | "question";
// Define the initial state
interface detailedMasterState {
  masters: {
    exams: ObjectValues[];
    goals: ObjectValues[];
    sections: ObjectValues[];
    units: ObjectValues[];
    topics: ObjectValues[];
    subtopics: ObjectValues[];
    clusters: ObjectValues[];
    questions: any;
  };
  masters_clone: {
    exams: ObjectValues[];
    goals: ObjectValues[];
    sections: ObjectValues[];
    units: ObjectValues[];
    topics: ObjectValues[];
    subtopics: ObjectValues[];
    clusters: ObjectValues[];
    questions: any;
  };
  loading: {
    exams: boolean;
    goals: boolean;
    sections: boolean;
    units: boolean;
    topics: boolean;
    subtopics: boolean;
    clusters: boolean;
    questions: boolean;
  };
  mocks: {
    series: ObjectValues[];
    mocktest: ObjectValues[];
    section: ObjectValues[];
    question: ObjectValues[];
  };
  mock_loading: {
    series: boolean;
    mocktest: boolean;
    section: boolean;
    question: boolean;
  };
}

const initialState: detailedMasterState = {
  masters: {
    exams: [],
    goals: [],
    sections: [],
    units: [],
    topics: [],
    subtopics: [],
    clusters: [],
    questions: { results: [] },
  },
  masters_clone: {
    exams: [],
    goals: [],
    sections: [],
    units: [],
    topics: [],
    subtopics: [],
    clusters: [],
    questions: { results: [] },
  },
  loading: {
    exams: false,
    goals: false,
    sections: false,
    units: false,
    topics: false,
    subtopics: false,
    clusters: false,
    questions: false,
  },
  mock_loading: {
    series: false,
    mocktest: false,
    section: false,
    question: false,
  },
  mocks: {
    series: [],
    mocktest: [],
    section: [],
    question: [],
  },
};
export const fetchAllMastersLocal = async ({
  level,
  parent_id,
}: {
  level: LevelTypes;
  parent_id?: string;
}) => {
  try {
    if (level) {
      if (level === "exam" || parent_id) {
        const lists: any = await apiRequest({
          api: allApis.detailed_masters(level, parent_id),
          method: "GET",
        });
        return { [`${level}s`]: [...(lists ?? [])] };
      } else {
        return { [`${level}s`]: [] };
      } // Assuming the API returns the data directly
    } else {
      return { [`${level}s`]: [] };
    }
  } catch (error: any) {
    return null;
  }
};
export const fetchAllMasters = createAsyncThunk(
  "detailedMaster/fetchAllMasters",
  async ({ level, parent_id }: { level: LevelTypes; parent_id?: string }) => {
    try {
      if (level) {
        if (level === "exam" || parent_id) {
          const lists: any = await apiRequest({
            api: allApis.detailed_masters(level, parent_id),
            method: "GET",
          });
          return {
            [`${level}s`]:
              level === "cluster"
                ? [...(lists ?? [])].map((s) => {
                    return { ...s, min_time: s.stc_min_time ?? s.min_time,
                      max_time: s.stc_max_time ?? s.max_time
                     };
                  })
                : [...(lists ?? [])],
          };
        } else {
          return { [`${level}s`]: [] };
        } // Assuming the API returns the data directly
      } else {
        return { [`${level}s`]: [] };
      }
    } catch (error: any) {
      return null;
    }
  }
);
export const fetchAllMastersForMocks = createAsyncThunk(
  "detailedMaster/fetchAllMastersForMocks",
  async ({
    level,
    parent_id,
    status,
  }: {
    level: MockLevelTypes;
    parent_id?: string;
    status?: string;
  }) => {
    try {
      if (level) {
        if (level === "series" || parent_id) {
          const lists: any = await apiRequest({
            api: allApis.detailed_mocks(level, parent_id, status),
            method: "GET",
          });
          return { [`${level}`]: [...(lists ?? [])] };
        } else {
          return { [`${level}`]: [] };
        } // Assuming the API returns the data directly
      } else {
        return { [`${level}`]: [] };
      }
    } catch (error: any) {
      return null;
    }
  }
);
export const fetchAllMastersForClone = createAsyncThunk(
  "detailedMaster/fetchAllMastersForClone",
  async ({ level, parent_id }: { level: LevelTypes; parent_id?: string }) => {
    try {
      if (level) {
        if (level === "exam" || parent_id) {
          const lists: any = await apiRequest({
            api: allApis.detailed_masters(level, parent_id),
            method: "GET",
          });
          return { [`${level}s`]: [...(lists ?? [])] };
        } else {
          return { [`${level}s`]: [] };
        } // Assuming the API returns the data directly
      } else {
        return { [`${level}s`]: [] };
      }
    } catch (error: any) {
      return null;
    }
  }
);
export const fetchClusterQuestionsforLocal = async ({
  level,
  parent_id,
}: {
  level: LevelTypes;
  parent_id?: string;
}) => {
  try {
    if (level) {
      if (parent_id) {
        const lists: any = await apiRequest({
          api: allApis.detailedQuestionslist(parent_id),
          method: "GET",
        });
        return { [`${level}s`]: lists };
      } else {
        return { [`${level}s`]: {} };
      } // Assuming the API returns the data directly
    } else {
      return { [`${level}s`]: {} };
    }
  } catch (error: any) {
    return null;
  }
};
export const fetchSinlgQustion = async (props: any) => {
  try {
    const response: any = await apiRequest(props);
    return response
      ? {
          ...response,
          languages_available:
            response.translation
              .map((s: { language: any }) => s.language)
              .join(",") ?? "english",
        }
      : null;
  } catch (error: any) {
    console.error("API Error:", error); // Add this line to catch and log any errors
    return null;
  }
};
export const fetchClusterQuestions = createAsyncThunk(
  "detailedMaster/fetchClusterQuestions",
  async ({
    level,
    parent_id,
    page,
  }: {
    level: LevelTypes;
    parent_id?: string;
    page?: number;
  }) => {
    try {
      if (level) {
        if (parent_id) {
          const lists: any = await apiRequest({
            api: allApis.detailedQuestionslist(parent_id, page),
            method: "GET",
          });
          return { [`${level}s`]: lists };
        } else {
          return { [`${level}s`]: {} };
        } // Assuming the API returns the data directly
      } else {
        return { [`${level}s`]: {} };
      }
    } catch (error: any) {
      return null;
    }
  }
);
const detailedMasterSlice = createSlice({
  name: "detailedMaster",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchAllMasters.pending, (state, action) => {
        state.loading = {
          ...state.loading,
          [action.meta.arg.level + "s"]: true,
        };
      })
      .addCase(fetchAllMasters.fulfilled, (state, action) => {
        state.loading = {
          ...state.loading,
          [action.meta.arg.level + "s"]: false,
        };
        state.masters = { ...state.masters, ...action.payload };
      })
      .addCase(fetchAllMasters.rejected, (state, action) => {
        state.loading = {
          ...state.loading,
          [action.meta.arg.level + "s"]: false,
        };
      })

      .addCase(fetchAllMastersForClone.pending, (state, action) => {
        state.loading = {
          ...state.loading,
          [action.meta.arg.level + "s"]: true,
        };
      })
      .addCase(fetchAllMastersForClone.fulfilled, (state, action) => {
        state.loading = {
          ...state.loading,
          [action.meta.arg.level + "s"]: false,
        };
        state.masters_clone = { ...state.masters_clone, ...action.payload };
      })
      .addCase(fetchAllMastersForClone.rejected, (state, action) => {
        state.loading = {
          ...state.loading,
          [action.meta.arg.level + "s"]: false,
        };
      })
      .addCase(fetchClusterQuestions.pending, (state, action) => {
        state.loading = {
          ...state.loading,
          [action.meta.arg.level + "s"]: true,
        };
      })
      .addCase(fetchClusterQuestions.fulfilled, (state, action) => {
        state.loading = {
          ...state.loading,
          [action.meta.arg.level + "s"]: false,
        };
        state.masters = { ...state.masters, ...action.payload };
      })
      .addCase(fetchClusterQuestions.rejected, (state, action) => {
        state.loading = {
          ...state.loading,
          [action.meta.arg.level + "s"]: false,
        };
      })
      .addCase(fetchAllMastersForMocks.pending, (state, action) => {
        state.mock_loading = {
          ...state.mock_loading,
          [action.meta.arg.level]: true,
        };
      })
      .addCase(fetchAllMastersForMocks.fulfilled, (state, action) => {
        state.mock_loading = {
          ...state.mock_loading,
          [action.meta.arg.level]: false,
        };
        state.mocks = { ...state.mocks, ...action.payload };
      })
      .addCase(fetchAllMastersForMocks.rejected, (state, action) => {
        state.mock_loading = {
          ...state.mock_loading,
          [action.meta.arg.level]: false,
        };
      });
  },
});

export const {} = detailedMasterSlice.actions;
export default detailedMasterSlice.reducer;
